/* FLAIR retailer locator: Shopify metaobjects + Mapbox GL JS.
   Data: all "retailer" metaobject entries via Storefront API, optionally
   hard-filtered to one locale (US, CA, EU, ...) so a locale page can never
   show stores from elsewhere. Store cards are built with DOM methods
   (no innerHTML) to keep admin-entered values inert. */
(function () {
  var root = document.querySelector('[data-retailer-locator]');
  if (!root) return;

  var cfg = {
    shop: root.dataset.shopDomain,
    token: root.dataset.storefrontToken,
    mapboxToken: root.dataset.mapboxToken,
    // Locale id (US, CA, EU, ...) or a bare country code; empty = no hard filter.
    localeFilter: (root.dataset.localeFilter || root.dataset.countryFilter || '').trim().toUpperCase(),
  };

  var els = {
    map: root.querySelector('[data-rl-map]'),
    list: root.querySelector('[data-rl-list]'),
    status: root.querySelector('[data-rl-status]'),
    filters: root.querySelector('[data-rl-filters]'),
    searchForm: root.querySelector('[data-rl-search]'),
    searchInput: root.querySelector('[data-rl-search-input]'),
    suggest: root.querySelector('[data-rl-suggest]'),
    geolocate: root.querySelector('[data-rl-geolocate]'),
  };

  if (!cfg.token || !cfg.mapboxToken) {
    els.status.textContent = 'Locator is not configured yet.';
    return;
  }

  var accent = getComputedStyle(root).getPropertyValue('--rl-accent').trim() || '#6B4C9F';
  var stores = [];
  var activeFilters = [];
  var map = null;

  // Locale personalization: US and Canada stand alone, everything else groups
  // into regions. Visitors from countries not listed just see all locations.
  var LOCALES = [
    { id: 'US', label: 'United States' },
    { id: 'CA', label: 'Canada' },
    { id: 'UKIE', label: 'UK & Ireland' },
    { id: 'EU', label: 'Europe' },
    { id: 'LATAM', label: 'Latin America & Caribbean' },
    { id: 'ME', label: 'Middle East' },
    { id: 'APAC', label: 'Asia-Pacific' },
    { id: 'AF', label: 'Africa' },
    { id: 'all', label: 'All locations' },
  ];
  var COUNTRY_LOCALE = {
    US: 'US', CA: 'CA',
    GB: 'UKIE', IE: 'UKIE',
    FR: 'EU', BE: 'EU', CH: 'EU', HU: 'EU', DE: 'EU', NL: 'EU',
    IT: 'EU', ES: 'EU', PT: 'EU', AT: 'EU', SE: 'EU', DK: 'EU', NO: 'EU', FI: 'EU',
    PL: 'EU', CZ: 'EU', LU: 'EU',
    MX: 'LATAM', PE: 'LATAM', CL: 'LATAM', VE: 'LATAM', PR: 'LATAM', UY: 'LATAM',
    AR: 'LATAM', CO: 'LATAM', BR: 'LATAM', EC: 'LATAM', CR: 'LATAM', PA: 'LATAM',
    SA: 'ME', KW: 'ME', BH: 'ME', QA: 'ME', AE: 'ME', AM: 'ME', IL: 'ME', JO: 'ME',
    OM: 'ME', LB: 'ME', TR: 'ME',
    IN: 'APAC', SG: 'APAC', AU: 'APAC', JP: 'APAC', NZ: 'APAC', CN: 'APAC',
    HK: 'APAC', KR: 'APAC', TH: 'APAC', MY: 'APAC', PH: 'APAC', ID: 'APAC',
    ZA: 'AF',
  };
  var activeLocale = 'all';
  var localeUI = null;
  var params = new URLSearchParams(location.search);
  var activeIdx = null;
  var pendingScrollIdx = null;
  var localeOf = function (countryCode) {
    return COUNTRY_LOCALE[(countryCode || '').toUpperCase()] || 'all';
  };
  var storeLocale = function (s) { return localeOf(s.country); };

  function track(name, eventParams) {
    if (typeof window.gtag === 'function') window.gtag('event', name, eventParams);
  }

  function safeUrl(value) {
    try {
      var u = new URL(value);
      if (u.protocol === 'http:' || u.protocol === 'https:') return u.href;
    } catch (e) { /* not a URL */ }
    return null;
  }

  function fetchPage(cursor) {
    var query =
      'query ($cursor: String) {' +
      ' metaobjects(type: "retailer", first: 250, after: $cursor) {' +
      '  pageInfo { hasNextPage endCursor }' +
      '  nodes { handle fields { key value } }' +
      ' }' +
      '}';
    return fetch('https://' + cfg.shop + '/api/2025-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({ query: query, variables: { cursor: cursor } }),
    }).then(function (res) { return res.json(); });
  }

  function fetchAllStores() {
    var out = [];
    function next(cursor) {
      return fetchPage(cursor).then(function (json) {
        var page = json.data && json.data.metaobjects;
        if (!page) throw new Error('Storefront API returned no data. Is storefront access enabled on the retailer definition?');
        page.nodes.forEach(function (node) {
          var s = { handle: node.handle };
          node.fields.forEach(function (f) { s[f.key] = f.value; });
          s.filters = s.filters ? JSON.parse(s.filters) : [];
          s.lat = parseFloat(s.latitude);
          s.lng = parseFloat(s.longitude);
          out.push(s);
        });
        return page.pageInfo.hasNextPage ? next(page.pageInfo.endCursor) : out;
      });
    }
    return next(null);
  }

  function haversineMiles(a, b) {
    var R = 3958.8;
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function filteredStores() {
    return stores.filter(function (s) {
      if (activeLocale !== 'all' && storeLocale(s) !== activeLocale) return false;
      return activeFilters.every(function (f) { return s.filters.indexOf(f) !== -1; });
    });
  }

  function toGeoJSON(list) {
    return {
      type: 'FeatureCollection',
      features: list.map(function (s) {
        return {
          type: 'Feature',
          properties: { idx: stores.indexOf(s) },
          geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
        };
      }),
    };
  }

  function addressLine(s) {
    return [s.address_1, s.address_2, s.city, s.region, s.postal_code]
      .filter(Boolean)
      .join(', ');
  }

  // Display phones in E.164-based form without touching stored values.
  // Returns null when unsure (odd digit counts, names mixed in), so the
  // original string is shown untouched.
  var NANP = { US: 1, CA: 1, PR: 1 };
  function formatPhone(raw, country) {
    var work = raw.replace(/\(0\)/g, ' ');
    var ext = '';
    var extMatch = work.match(/(?:ext\.?|x)\s*(\d+)/i);
    if (extMatch) {
      ext = ' ext. ' + extMatch[1];
      work = work.replace(extMatch[0], ' ');
    }
    work = work.replace(/^\s*00[\s.-]/, '+');
    if (/[a-z]/i.test(work)) return null;
    var digits = work.replace(/[^0-9]/g, '');
    var hasPlus = work.indexOf('+') !== -1;
    var e164 = null;
    var display = null;
    if (NANP[(country || '').toUpperCase()]) {
      if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
      if (digits.length === 10) {
        e164 = '+1' + digits;
        display = '+1 (' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
      }
    } else if ((hasPlus || country) && digits.length >= 8 && digits.length <= 15) {
      e164 = '+' + digits;
      display = e164;
    }
    return display ? { display: display + ext, href: 'tel:' + e164 } : null;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function storeCardNode(s, distance) {
    var card = el('div', 'rl__card');
    card.appendChild(el('h3', 'rl__store-name', s.name || ''));
    if (distance != null) card.appendChild(el('span', 'rl__distance', distance.toFixed(1) + ' mi'));
    card.appendChild(el('p', 'rl__address', addressLine(s)));

    var links = el('p', 'rl__links');
    if (s.phone) {
      var formatted = formatPhone(s.phone, s.country);
      var tel = el('a', null, formatted ? formatted.display : s.phone);
      tel.href = formatted ? formatted.href : 'tel:' + s.phone.replace(/[^0-9+]/g, '');
      links.appendChild(tel);
    }
    var directions = el('a', null, 'Directions');
    directions.href = 'https://www.google.com/maps/dir/?api=1&destination=' + s.lat + ',' + s.lng;
    directions.target = '_blank';
    directions.rel = 'noopener';
    directions.addEventListener('click', function () {
      track('locator_store_click', { store_name: s.name });
    });
    if (links.childNodes.length) links.appendChild(document.createTextNode(' · '));
    links.appendChild(directions);

    [['website', 'Website'], ['facebook', 'Facebook']].forEach(function (pair) {
      var url = s[pair[0]] && safeUrl(s[pair[0]]);
      if (!url) return;
      var a = el('a', null, pair[1]);
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.addEventListener('click', function () {
        track('locator_store_click', { store_name: s.name });
      });
      if (links.childNodes.length) links.appendChild(document.createTextNode(' · '));
      links.appendChild(a);
    });
    if (links.childNodes.length) card.appendChild(links);
    return card;
  }

  // Lightweight, non-interactive preview shown on pin hover. The full card
  // (with clickable links) still opens on click via focusStore.
  function storeHoverNode(s, extra) {
    var box = el('div', 'rl__hovercard');
    box.appendChild(el('strong', 'rl__store-name', s.name || ''));
    box.appendChild(el('p', 'rl__address', addressLine(s)));
    if (extra > 0) {
      box.appendChild(el('p', 'rl__hovercard-more', '+ ' + extra + ' more here · click to view'));
    }
    return box;
  }

  function renderList() {
    var center = map ? { lng: map.getCenter().lng, lat: map.getCenter().lat } : null;
    var list = filteredStores().slice();
    if (center) {
      list.sort(function (a, b) {
        return haversineMiles(center, a) - haversineMiles(center, b);
      });
    }
    els.list.textContent = '';
    list.forEach(function (s) {
      var idx = stores.indexOf(s);
      var li = el('li', 'rl__store');
      li.dataset.idx = idx;
      if (idx === activeIdx) li.classList.add('rl__store--active');
      li.appendChild(storeCardNode(s, center ? haversineMiles(center, s) : null));
      li.addEventListener('click', function (e) {
        if (e.target.closest('a')) return;
        focusStore(s);
        track('locator_store_click', { store_name: s.name });
      });
      li.addEventListener('mouseenter', function () { highlightStore(s); });
      li.addEventListener('mouseleave', function () { highlightStore(null); });
      els.list.appendChild(li);
    });
    els.status.textContent = list.length + ' location' + (list.length === 1 ? '' : 's');
    if (pendingScrollIdx !== null) {
      var active = els.list.querySelector('[data-idx="' + pendingScrollIdx + '"]');
      if (active) active.scrollIntoView({ block: 'nearest' });
      pendingScrollIdx = null;
    }
  }

  function highlightStore(s) {
    if (!map || !map.getSource('highlight')) return;
    map.getSource('highlight').setData({
      type: 'FeatureCollection',
      features: s ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [s.lng, s.lat] } }] : [],
    });
  }

  function focusStore(s) {
    activeIdx = stores.indexOf(s);
    pendingScrollIdx = activeIdx;
    map.flyTo({ center: [s.lng, s.lat], zoom: 12 });
    new mapboxgl.Popup({ offset: 12, maxWidth: '280px' })
      .setLngLat([s.lng, s.lat])
      .setDOMContent(storeCardNode(s, null))
      .addTo(map);
  }

  function setLocale(id, method, opts) {
    if (method === 'manual') {
      try { localStorage.setItem('rl-locale', id); } catch (e) { /* private mode */ }
    }
    if (id === activeLocale) return;
    activeLocale = id;
    if (localeUI) {
      var label = LOCALES.filter(function (l) { return l.id === id; })[0].label;
      localeUI.current.textContent = id === 'all' ? 'Showing all locations' : 'Showing locations in ' + label;
      localeUI.menu.querySelectorAll('button').forEach(function (b) {
        b.classList.toggle('rl__locale-option--active', b.dataset.locale === id);
      });
      localeUI.menu.hidden = true;
    }
    buildFilterCheckboxes();
    refresh();
    if (!(opts && opts.skipFit)) fitToStores();
    track('locator_locale_switch', { locale: id, method: method });
  }

  function buildLocaleBar() {
    if (cfg.localeFilter) return; // hard-filtered locale pages never show the switcher
    var counts = {};
    stores.forEach(function (s) { counts[storeLocale(s)] = (counts[storeLocale(s)] || 0) + 1; });
    counts.all = stores.length;
    var available = LOCALES.filter(function (l) { return counts[l.id]; });
    if (available.length < 3) return; // just "all" and one locale: pointless

    var bar = el('div', 'rl__locale');
    var current = el('span', 'rl__locale-current', 'Showing all locations');
    var toggle = el('button', 'rl__locale-toggle', 'Looking for locations elsewhere?');
    toggle.type = 'button';
    var menu = el('div', 'rl__locale-menu');
    menu.hidden = true;
    available.forEach(function (l) {
      var b = el('button', 'rl__locale-option', l.label + ' (' + counts[l.id] + ')');
      b.type = 'button';
      b.dataset.locale = l.id;
      b.addEventListener('click', function () {
        menu.hidden = true;
        setLocale(l.id, 'manual');
      });
      menu.appendChild(b);
    });
    toggle.addEventListener('click', function (e) {
      e.stopPropagation();
      menu.hidden = !menu.hidden;
    });
    document.addEventListener('click', function (e) {
      if (!menu.hidden && !menu.contains(e.target)) menu.hidden = true;
    });
    bar.appendChild(current);
    bar.appendChild(toggle);
    bar.appendChild(menu);
    els.filters.parentNode.insertBefore(bar, els.filters);
    localeUI = { current: current, menu: menu };
  }

  function localeHasStores(locale) {
    return stores.some(function (s) { return storeLocale(s) === locale; });
  }

  // Priority: ?locale= deep link, then a saved manual choice, then IP detection.
  function initLocale() {
    if (cfg.localeFilter || !localeUI) return;
    var param = (params.get('locale') || '').trim().toUpperCase();
    if (param) {
      var isLocaleId = LOCALES.some(function (l) { return l.id === param; });
      var target = param === 'ALL' ? 'all' : isLocaleId ? param : localeOf(param);
      if (target === 'all' || localeHasStores(target)) {
        setLocale(target, 'deep_link');
        return;
      }
    }
    var saved = null;
    try { saved = localStorage.getItem('rl-locale'); } catch (e) { /* private mode */ }
    if (saved && (saved === 'all' || (LOCALES.some(function (l) { return l.id === saved; }) && localeHasStores(saved)))) {
      setLocale(saved, 'saved');
      return;
    }
    fetch('/browsing_context_suggestions.json')
      .then(function (res) { return res.json(); })
      .then(function (json) {
        var country = json.detected_values && json.detected_values.country && json.detected_values.country.handle;
        var locale = localeOf(country);
        if (locale !== 'all' && localeHasStores(locale)) {
          setLocale(locale, 'auto');
        }
      })
      .catch(function () { /* stay on all locations */ });
  }

  // On nearly every store, so it adds no filtering signal; hide from the UI.
  var HIDDEN_FILTERS = ['FLAIR® Strips'];

  function buildFilterCheckboxes() {
    var scope = stores.filter(function (s) {
      return activeLocale === 'all' || storeLocale(s) === activeLocale;
    });
    var counts = {};
    scope.forEach(function (s) {
      s.filters.forEach(function (f) {
        if (HIDDEN_FILTERS.indexOf(f) !== -1) return;
        counts[f] = (counts[f] || 0) + 1;
      });
    });
    // Drop any active filter that has no matches in the current locale.
    activeFilters = activeFilters.filter(function (f) { return counts[f]; });
    // Clear the previous checkboxes but keep the <legend>.
    Array.prototype.forEach.call(els.filters.querySelectorAll('.rl__filter'), function (n) { n.remove(); });
    Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; }).forEach(function (f) {
      var label = el('label', 'rl__filter');
      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.value = f;
      cb.checked = activeFilters.indexOf(f) !== -1;
      cb.addEventListener('change', function () {
        activeFilters = Array.prototype.map.call(
          els.filters.querySelectorAll('input:checked'),
          function (i) { return i.value; }
        );
        refresh();
      });
      label.appendChild(cb);
      label.appendChild(document.createTextNode(' ' + f + ' (' + counts[f] + ')'));
      els.filters.appendChild(label);
    });
  }

  function refresh() {
    if (map && map.getSource('stores')) {
      map.getSource('stores').setData(toGeoJSON(filteredStores()));
    }
    renderList();
  }

  function fitToStores() {
    var list = filteredStores();
    if (!list.length) return;
    var bounds = new mapboxgl.LngLatBounds();
    list.forEach(function (s) { bounds.extend([s.lng, s.lat]); });
    map.fitBounds(bounds, { padding: 48, maxZoom: 10, duration: 0 });
  }

  function initMap() {
    mapboxgl.accessToken = cfg.mapboxToken;
    map = new mapboxgl.Map({
      container: els.map,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-98, 45],
      zoom: 3,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', function () {
      map.addSource('stores', {
        type: 'geojson',
        data: toGeoJSON(filteredStores()),
        cluster: true,
        clusterMaxZoom: 11,
        clusterRadius: 45,
      });
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'stores',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': accent,
          'circle-opacity': 0.85,
          'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 28],
        },
      });
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'stores',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 13,
        },
        paint: { 'text-color': '#ffffff' },
      });
      map.addLayer({
        id: 'store-point',
        type: 'circle',
        source: 'stores',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': accent,
          'circle-radius': 7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      map.on('click', 'clusters', function (e) {
        var feature = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
        map.getSource('stores').getClusterExpansionZoom(feature.properties.cluster_id, function (err, zoom) {
          if (err) return;
          map.easeTo({ center: feature.geometry.coordinates, zoom: zoom });
        });
      });
      map.addSource('highlight', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'highlight-point',
        type: 'circle',
        source: 'highlight',
        paint: {
          'circle-color': accent,
          'circle-radius': 11,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      map.on('click', 'store-point', function (e) {
        // Several stores can share one exact point (same building); collect
        // every distinct store under the cursor instead of just the top pin.
        var seen = {};
        var hits = [];
        e.features.forEach(function (f) {
          if (!seen[f.properties.idx]) {
            seen[f.properties.idx] = true;
            hits.push(stores[f.properties.idx]);
          }
        });
        if (hits.length === 1) {
          focusStore(hits[0]);
          track('locator_store_click', { store_name: hits[0].name });
          return;
        }
        var box = el('div', 'rl__multi');
        box.appendChild(el('p', 'rl__multi-title', hits.length + ' locations at this spot'));
        var popup = new mapboxgl.Popup({ offset: 12, maxWidth: '280px' })
          .setLngLat(e.lngLat)
          .setDOMContent(box);
        hits.forEach(function (s) {
          var b = el('button', 'rl__multi-option', s.name);
          b.type = 'button';
          b.addEventListener('click', function () {
            popup.remove();
            focusStore(s);
            track('locator_store_click', { store_name: s.name });
          });
          box.appendChild(b);
        });
        popup.addTo(map);
      });
      map.on('mouseenter', 'clusters', function () { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'clusters', function () { map.getCanvas().style.cursor = ''; });

      // Pin hover: preview the store(s) under the cursor without zooming in.
      var hoverPopup = new mapboxgl.Popup({
        offset: 12,
        maxWidth: '260px',
        closeButton: false,
        closeOnClick: false,
        className: 'rl__popup rl__popup--hover',
      });
      map.on('mousemove', 'store-point', function (e) {
        map.getCanvas().style.cursor = 'pointer';
        var seen = {};
        var hits = [];
        e.features.forEach(function (f) {
          if (!seen[f.properties.idx]) {
            seen[f.properties.idx] = true;
            hits.push(stores[f.properties.idx]);
          }
        });
        if (!hits.length) return;
        var s = hits[0];
        highlightStore(s);
        hoverPopup.setLngLat([s.lng, s.lat]).setDOMContent(storeHoverNode(s, hits.length - 1)).addTo(map);
      });
      map.on('mouseleave', 'store-point', function () {
        map.getCanvas().style.cursor = '';
        highlightStore(null);
        hoverPopup.remove();
      });

      fitToStores();
      renderList();
    });

    var moveTimer = null;
    map.on('moveend', function () {
      clearTimeout(moveTimer);
      moveTimer = setTimeout(renderList, 150);
    });
  }

  // Countries to restrict geocoding to when the page is hard-filtered.
  function filterCountries() {
    if (!cfg.localeFilter) return '';
    var list = Object.keys(COUNTRY_LOCALE).filter(function (c) {
      return COUNTRY_LOCALE[c] === cfg.localeFilter;
    });
    return list.length ? list.join(',') : cfg.localeFilter;
  }

  // Countries to bias Mapbox results toward: the hard-filter list on a locale
  // page, otherwise the active locale's countries on the global page.
  function localeCountries(loc) {
    return Object.keys(COUNTRY_LOCALE).filter(function (c) { return COUNTRY_LOCALE[c] === loc; });
  }
  function biasCountries() {
    if (cfg.localeFilter) return filterCountries();
    if (activeLocale !== 'all') return localeCountries(activeLocale).join(',');
    return '';
  }

  function geocodeForward(q, limit) {
    var params = new URLSearchParams({ q: q, limit: String(limit), access_token: cfg.mapboxToken });
    if (limit > 1) params.set('autocomplete', 'true');
    var fc = biasCountries();
    if (fc) params.set('country', fc);
    return fetch('https://api.mapbox.com/search/geocode/v6/forward?' + params)
      .then(function (res) { return res.json(); })
      .then(function (json) { return json.features || []; })
      .catch(function () { return []; });
  }

  function storeNameMatches(q) {
    var ql = q.toLowerCase();
    return stores.filter(function (s) {
      if (activeLocale !== 'all' && storeLocale(s) !== activeLocale) return false;
      return (s.name || '').toLowerCase().indexOf(ql) !== -1;
    });
  }

  // Fly to a geocoded place, switching locale if it sits outside the active one
  // so the list never looks mysteriously empty.
  function goToPlace(feature, q) {
    var ctx = feature.properties.context;
    var resultCountry = ctx && ctx.country && ctx.country.country_code;
    if (!cfg.localeFilter && activeLocale !== 'all' && resultCountry && localeOf(resultCountry) !== activeLocale) {
      var target = localeOf(resultCountry);
      var hasStores = stores.some(function (s) { return storeLocale(s) === target; });
      setLocale(hasStores && target !== 'all' ? target : 'all', 'search', { skipFit: true });
    }
    var c = feature.properties.coordinates;
    var point = { lng: c.longitude, lat: c.latitude };
    var nearby = filteredStores().filter(function (s) { return haversineMiles(point, s) <= 50; }).length;
    track('locator_search', { query: q, result_count: nearby, mode: 'place' });
    map.flyTo({ center: [point.lng, point.lat], zoom: 9 });
  }

  function runSearch(q) {
    var hits = storeNameMatches(q);
    if (hits.length === 1) {
      focusStore(hits[0]);
      track('locator_search', { query: q, result_count: 1, mode: 'name' });
      return;
    }
    geocodeForward(q, 1).then(function (features) {
      if (features[0]) { goToPlace(features[0], q); return; }
      if (hits.length > 1) {
        var b = new mapboxgl.LngLatBounds();
        hits.forEach(function (s) { b.extend([s.lng, s.lat]); });
        map.fitBounds(b, { padding: 48, maxZoom: 12, duration: 400 });
        els.status.textContent = hits.length + ' locations matching "' + q + '"';
        track('locator_search', { query: q, result_count: hits.length, mode: 'name' });
        return;
      }
      els.status.textContent = 'No matching area found' + (cfg.localeFilter ? ' for this region' : '') + '.';
      track('locator_search', { query: q, result_count: 0 });
    });
  }

  // ---- autocomplete (store names + Mapbox place suggestions) ----
  function closeSuggest() { els.suggest.hidden = true; els.suggest.textContent = ''; }

  function suggestItem(label, sub, onpick) {
    var item = el('button', 'rl__suggest-item');
    item.type = 'button';
    item.appendChild(el('span', 'rl__suggest-name', label));
    if (sub) item.appendChild(el('span', 'rl__suggest-sub', sub));
    item.addEventListener('click', function () {
      onpick();
      closeSuggest();
    });
    els.suggest.appendChild(item);
  }

  function renderSuggest(names, places) {
    els.suggest.textContent = '';
    if (!names.length && !places.length) { closeSuggest(); return; }
    if (names.length) {
      els.suggest.appendChild(el('div', 'rl__suggest-label', 'Stores'));
      names.forEach(function (s) {
        var sub = [s.city, s.region].filter(Boolean).join(', ');
        suggestItem(s.name, sub, function () { els.searchInput.value = s.name; focusStore(s); });
      });
    }
    if (places.length) {
      els.suggest.appendChild(el('div', 'rl__suggest-label', 'Places'));
      places.forEach(function (f) {
        var name = f.properties.name || f.properties.full_address || '';
        suggestItem(name, f.properties.place_formatted || '', function () {
          els.searchInput.value = name;
          goToPlace(f, name);
        });
      });
    }
    els.suggest.hidden = false;
  }

  var suggestTimer = null;
  els.searchInput.addEventListener('input', function () {
    var q = els.searchInput.value.trim();
    clearTimeout(suggestTimer);
    if (q.length < 2) { closeSuggest(); return; }
    renderSuggest(storeNameMatches(q).slice(0, 5), []); // instant store matches
    suggestTimer = setTimeout(function () {
      geocodeForward(q, 5).then(function (places) {
        if (els.searchInput.value.trim() !== q) return; // input moved on
        renderSuggest(storeNameMatches(q).slice(0, 5), places.slice(0, 5));
      });
    }, 220);
  });
  els.searchInput.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeSuggest();
  });
  document.addEventListener('click', function (e) {
    if (!els.searchForm.contains(e.target)) closeSuggest();
  });

  els.searchForm.addEventListener('submit', function (e) {
    e.preventDefault();
    closeSuggest();
    var q = els.searchInput.value.trim();
    if (q) runSearch(q);
  });

  els.geolocate.addEventListener('click', function () {
    if (!navigator.geolocation) return;
    els.geolocate.disabled = true;
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        els.geolocate.disabled = false;
        map.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 9 });
      },
      function () {
        els.geolocate.disabled = false;
        els.status.textContent = 'Could not get your location.';
      }
    );
  });

  fetchAllStores()
    .then(function (all) {
      var withCoords = all.filter(function (s) { return isFinite(s.lat) && isFinite(s.lng); });
      if (withCoords.length < all.length) {
        console.warn('[retailer-locator] ' + (all.length - withCoords.length) + ' entries missing coordinates were skipped.');
      }
      var filterIsRegion = LOCALES.some(function (l) { return l.id === cfg.localeFilter; });
      stores = !cfg.localeFilter ? withCoords : withCoords.filter(function (s) {
        return filterIsRegion
          ? storeLocale(s) === cfg.localeFilter
          : (s.country || '').toUpperCase() === cfg.localeFilter;
      });
      buildLocaleBar();
      buildFilterCheckboxes();
      initMap();
      initLocale();
      var deepQuery = (params.get('q') || '').trim();
      if (deepQuery) {
        els.searchInput.value = deepQuery;
        runSearch(deepQuery);
      }
    })
    .catch(function (err) {
      console.error('[retailer-locator]', err);
      els.status.textContent = 'Locations could not be loaded. Please try again later.';
    });
})();
